import { DEFAULT_EMP_SPOUSE_RATES_DIFFER } from "./constants";
import { RateScheduleFormData } from "./types/models";
import { mapToBackendUnit } from "./utils/rate-helpers";

/**
 * Standard 13-band age ranges used by most age-banded coverages
 * (Voluntary Life, STD, LTD).
 */
const STANDARD_AGE_BANDS: { lower: number; upper: number }[] = [
  { lower: 0, upper: 20 },
  { lower: 20, upper: 24 },
  { lower: 25, upper: 29 },
  { lower: 30, upper: 34 },
  { lower: 35, upper: 39 },
  { lower: 40, upper: 44 },
  { lower: 45, upper: 49 },
  { lower: 50, upper: 54 },
  { lower: 55, upper: 59 },
  { lower: 60, upper: 64 },
  { lower: 65, upper: 69 },
  { lower: 70, upper: 74 },
  { lower: 75, upper: 150 },
];

/**
 * Critical Illness uses single-age milestone bands (lower === upper).
 */
const CRITICAL_ILLNESS_AGE_BANDS: { lower: number; upper: number }[] = [
  { lower: 25, upper: 25 },
  { lower: 35, upper: 35 },
  { lower: 45, upper: 45 },
  { lower: 55, upper: 55 },
  { lower: 65, upper: 65 },
];

/** Standard 4-tier coverage tiers */
const STANDARD_4_TIERS = [
  "employee",
  "employeeSpouse",
  "employeeChildren",
  "employeeFamily",
];

/** Critical Illness 3-tier coverage tiers */
const CRITICAL_ILLNESS_TIERS = ["employee", "spouseOnly", "childOnly"];

/**
 * Default RateScheduleFormData for each coverage type.
 * Used when initializing form data for a plan that has no existing rate schedule.
 *
 * Coverage types not listed here will initialize with an empty `{}`.
 */
const COVERAGE_DEFAULTS: Record<string, RateScheduleFormData> = {
  // ─── Dental: Tiered, 4-Tier, USD ──────────────────────────────────
  dental: {
    rateStructure: "tiered",
    numberOfTiers: 4,
    selectedTiers: STANDARD_4_TIERS,
    unit: "usd",
  },
  "dental-ppo": {
    rateStructure: "tiered",
    numberOfTiers: 4,
    selectedTiers: STANDARD_4_TIERS,
    unit: "usd",
  },
  "dental-hmo": {
    rateStructure: "tiered",
    numberOfTiers: 4,
    selectedTiers: STANDARD_4_TIERS,
    unit: "usd",
  },

  // ─── Vision: Tiered, 4-Tier, USD ──────────────────────────────────
  vision: {
    rateStructure: "tiered",
    numberOfTiers: 4,
    selectedTiers: STANDARD_4_TIERS,
    unit: "usd",
  },

  // ─── Basic Life: Flat Rate, Covered Benefit, Per $1000, AD&D included, no dependents ──
  basiclifeadnd: {
    rateStructure: "flat",
    structureDetail: "none",
    includeDependentRates: false,
    ratesBasedOn: "volume",
    coveredBenefitType: "coveredBenefit",
    ratePer: "per1000",
    unit: mapToBackendUnit("coveredBenefit", "per1000"),
    adndRatesApply: true,
    dependentAdndRatesApply: false,
  },

  // ─── Voluntary Life: Age Banded, standard bands, dependents separate, volume/per $1000, AD&D + dep AD&D ──
  voluntarylifeadnd: {
    rateStructure: "ageBanded",
    structureDetail: "none",
    ageBandRanges: STANDARD_AGE_BANDS,
    includeDependentRates: true,
    dependentRateType: "separate",
    ratesBasedOn: "volume",
    coveredBenefitType: "coveredBenefit",
    ratePer: "per1000",
    unit: mapToBackendUnit("coveredBenefit", "per1000"),
    adndRatesApply: true,
    dependentAdndRatesApply: true,
    dependentAdndType: "separate",
    empSpouseRatesDiffer: DEFAULT_EMP_SPOUSE_RATES_DIFFER,
  },

  // ─── Short Term Disability: Age Banded, standard bands, no dependents, Covered Benefit, Per $10 ──
  std: {
    rateStructure: "ageBanded",
    structureDetail: "none",
    ageBandRanges: STANDARD_AGE_BANDS,
    includeDependentRates: false,
    ratesBasedOn: "volume",
    coveredBenefitType: "coveredBenefit",
    ratePer: "per10",
    unit: mapToBackendUnit("coveredBenefit", "per10"),
  },

  // ─── Long Term Disability: Age Banded, standard bands, no dependents, Covered Payroll, Per $100 ──
  ltd: {
    rateStructure: "ageBanded",
    structureDetail: "none",
    ageBandRanges: STANDARD_AGE_BANDS,
    includeDependentRates: false,
    ratesBasedOn: "volume",
    coveredBenefitType: "coveredPayroll",
    ratePer: "per100",
    unit: mapToBackendUnit("coveredPayroll", "per100"),
  },

  // ─── Accident: Tiered, 4-Tier, USD ────────────────────────────────
  accident: {
    rateStructure: "tiered",
    numberOfTiers: 4,
    selectedTiers: STANDARD_4_TIERS,
    unit: "usd",
  },

  // ─── Critical Illness: Age Banded + 3-Tier, milestone bands, dependents separate, Covered Benefit, Per $1000 ──
  criticalillness: {
    rateStructure: "ageBanded",
    structureDetail: "tiered",
    ageBandRanges: CRITICAL_ILLNESS_AGE_BANDS,
    numberOfTiers: 3,
    selectedTiers: CRITICAL_ILLNESS_TIERS,
    includeDependentRates: true,
    dependentRateType: "separate",
    ratesBasedOn: "volume",
    coveredBenefitType: "coveredBenefit",
    ratePer: "per1000",
    unit: mapToBackendUnit("coveredBenefit", "per1000"),
  },

  // ─── Hospital Indemnity: Tiered, 4-Tier, USD ──────────────────────
  hospitalindemnity: {
    rateStructure: "tiered",
    numberOfTiers: 4,
    selectedTiers: STANDARD_4_TIERS,
    unit: "usd",
  },

  // ─── Cancer: Tiered, 4-Tier, USD ──────────────────────────────────
  cancer: {
    rateStructure: "tiered",
    numberOfTiers: 4,
    selectedTiers: STANDARD_4_TIERS,
    unit: "usd",
  },

  // ─── EAP: PEPM, no structure detail ───────────────────────────────
  eap: {
    rateStructure: "pepm",
    structureDetail: "none",
  },

  // ─── FMLA: PEPM, no structure detail ──────────────────────────────
  fmla: {
    rateStructure: "pepm",
    structureDetail: "none",
  },
};

/**
 * Returns default RateScheduleFormData for a given coverage type.
 * Falls back to `{}` if no defaults are defined for the coverage.
 */
export function getDefaultFormDataForCoverage(
  coverageType?: string,
): RateScheduleFormData {
  if (!coverageType) {
    return {};
  }

  const defaults = COVERAGE_DEFAULTS[coverageType.toLowerCase()];
  if (!defaults) {
    return {};
  }

  return structuredClone(defaults);
}
